/* EYEKØNIK size guide. Canonical values stay in mm; only presentation is rounded. */
if (!customElements.get('eyk-size-guide')) {
  class EykSizeGuide extends HTMLElement {
    static parse(value, unit = 'mm') {
      const text = String(value ?? '').trim().replace(',', '.');
      if (!/^(?:\d+(?:\.\d*)?|\.\d+)$/.test(text)) return null;
      const mm = Number(text) * (unit === 'in' ? 25.4 : 1);
      return Number.isFinite(mm) && mm > 0 ? mm : null;
    }

    static format(mm, unit = 'mm') {
      return Number((mm / (unit === 'in' ? 25.4 : 1)).toFixed(unit === 'in' ? 2 : 1)).toString();
    }

    static tokens(template, values) {
      return template.replace(/\[([a-z_]+)\]/g, (match, key) => values[key] ?? match);
    }

    static difference(productMm, currentMm, length = false, unit = 'mm') {
      const delta = productMm - currentMm;
      const diff = EykSizeGuide.format(Math.abs(delta), unit);
      const direction = delta > 0 ? (length ? 'longer' : 'wider') : (length ? 'shorter' : 'narrower');
      // Ignore floating-point noise, never round stored measurements to decide direction.
      const equal = Math.abs(delta) <= Number.EPSILON * Math.max(productMm, currentMm) * 4;
      return {
        diff,
        direction: equal ? (length ? 'the same length' : 'the same width') : direction,
        comparison: equal
          ? `the same ${length ? 'length' : 'width'} as your current pair`
          : `${diff === '0' ? (unit === 'in' ? 'less than 0.01' : 'less than 0.1') : diff} ${unit} ${direction} than your current pair`
      };
    }

    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.unit = this.dataset.units === 'in' ? 'in' : 'mm';
      this.modal = this.querySelector('modal-dialog');
      this.opener = this.querySelector('.eyk-size-guide__link');
      if (!this.modal || !this.opener) {
        this.controller.abort();
        this.controller = null;
        return;
      }
      const section = this.closest('.section-main-product, .shopify-section');
      this.extraOpeners = section ? [...section.querySelectorAll('[data-eyk-size-guide-opener]')]
        .filter((button) => button.closest('.section-main-product, .shopify-section') === section) : [];
      this.openers = [this.opener, ...this.extraOpeners];
      this.extraOpeners.forEach((button) => button.setAttribute('aria-controls', this.modal.id));
      this.modalHome = document.createComment('Size guide modal home');
      this.modal.before(this.modalHome);
      document.body.append(this.modal);
      this.bindColumnLayout(signal);
      this.bindStickyLine(signal);
      this.bindDiagramBounds(signal);
      this.rows = [...this.modal.querySelectorAll('tr[data-dimension]')];
      this.lines = [...this.modal.querySelectorAll('[data-line]')];
      this.pointers = [...this.modal.querySelectorAll('[data-pointer]')];
      this.fields = [...this.modal.querySelectorAll('form[data-compare]')].map((form) => {
        const input = form.querySelector('input');
        const row = this.rows.find((item) => item.dataset.dimension === form.dataset.compare);
        const field = { form, input, row, mm: EykSizeGuide.parse(input.value, this.unit), productMm: EykSizeGuide.parse(row?.dataset.mm), submitted: false };
        input.addEventListener('input', () => {
          field.mm = EykSizeGuide.parse(input.value, this.unit);
          if (field.submitted) this.compare(field);
          else this.clearError(field);
        }, { signal });
        input.addEventListener('focus', () => this.keepInputVisible(), { signal });
        form.addEventListener('submit', (event) => {
          event.preventDefault();
          field.submitted = true;
          this.compare(field);
          if (field.mm === null) input.focus();
        }, { signal });
        return field;
      });
      this.bindTempleCompare(signal);
      // One capture guard protects both stock and section-local openers.
      this.trigger = this.opener.closest('open-or-scroll-to');
      this.openers.forEach((opener) => {
        const trigger = opener === this.opener ? this.trigger : opener;
        trigger.addEventListener('click', (event) => {
          this.openedAt = Date.now();
          this.keyboardHighlight = false;
          this.pinnedDimension = null;
          this.highlight(null);
          const target = document.getElementById(opener.getAttribute('aria-controls'));
          const anotherModal = [...document.querySelectorAll('.modal[open]')].some((item) => item !== target);
          if (!target || typeof target.open !== 'function' || target.hasAttribute('open') || anotherModal) {
            event.preventDefault();
            event.stopImmediatePropagation();
          }
        }, { capture: true, signal });
        if (opener !== this.opener) {
          // Stock Modal retains this exact button for focus restoration on close.
          opener.addEventListener('click', () => this.modal.open(opener), { signal });
        }
      });
      this.modal.querySelectorAll('[data-unit]').forEach((button) => {
        button.addEventListener('click', () => this.changeUnit(button.dataset.unit), { signal });
      });
      this.bindHighlights(signal);
      this.bindPurchase(signal);
      this.resizeViewport = () => {
        const viewport = window.visualViewport;
        if (viewport) {
          this.modal.style.setProperty('--eyk-sg-viewport-height', `${viewport.height}px`);
          this.modal.style.setProperty('--eyk-sg-viewport-top', `${viewport.offsetTop}px`);
        }
        this.keepInputVisible();
      };
      window.visualViewport?.addEventListener('resize', this.resizeViewport, { signal });
      window.visualViewport?.addEventListener('scroll', this.resizeViewport, { signal });
      this.resizeViewport();
      this.renderUnits();
      this.renderCopy();
      this.placeInlineTrigger();
      Promise.all([customElements.whenDefined('modal-dialog'), customElements.whenDefined('open-or-scroll-to')]).then(() => {
        if (!signal.aborted) this.openers.forEach((opener) => {
          opener.hidden = false;
          opener.disabled = false;
        });
      });
    }

    disconnectedCallback() {
      this.swatchObserver?.disconnect();
      this.purchaseObserver?.disconnect();
      this.diagramResizeObserver?.disconnect();
      this.diagramOpenObserver?.disconnect();
      this.titleResizeObserver?.disconnect();
      this.titleOpenObserver?.disconnect();
      cancelAnimationFrame(this.diagramFrame);
      this.restoreInlineTrigger();
      if (this.modal?.hasAttribute('open') && typeof this.modal.close === 'function') this.modal.close();
      this.openers?.forEach((opener) => {
        opener.hidden = true;
        opener.disabled = true;
      });
      this.extraOpeners?.forEach((opener) => opener.removeAttribute('aria-controls'));
      this.controller?.abort();
      this.controller = null;
      cancelAnimationFrame(this.focusFrame);
      if (this.modalHome?.parentNode) this.modalHome.replaceWith(this.modal);
      else this.modal?.remove();
      this.modalHome = null;
      this.triggerHome?.remove();
      this.triggerHome = null;
      this.pinnedDimension = null;
    }

    bindColumnLayout(signal) {
      const columns = this.modal.querySelector('.eyk-size-guide__columns');
      const table = columns?.querySelector('.eyk-size-guide__measurements');
      const front = columns?.querySelector('.eyk-size-guide__front');
      const side = columns?.querySelector('.eyk-size-guide__side');
      const compare = columns?.querySelector('.eyk-size-guide__compare');
      const fit = columns?.querySelector('.eyk-size-guide__fit');
      if (!columns || !table || !front || !side) return;
      const left = document.createElement('div');
      const right = document.createElement('div');
      left.className = right.className = 'eyk-size-guide__column';
      const desktop = window.matchMedia('(min-width: 900px)');
      const arrange = () => {
        // Move originals only: independent desktop columns, original mobile order.
        if (desktop.matches) {
          left.append(...[table, compare].filter(Boolean));
          right.append(...[fit, front, side].filter(Boolean));
          columns.replaceChildren(left, right);
        } else {
          columns.replaceChildren(...[table, fit, front, side, compare].filter(Boolean));
        }
      };
      desktop.addEventListener('change', arrange, { signal });
      arrange();
    }

    bindStickyLine(signal) {
      const title = this.modal.querySelector('.eyk-size-guide__product');
      if (!title) return;
      const sync = () => {
        if (signal.aborted) return;
        const height = title.getBoundingClientRect().height;
        if (height > 0) this.modal.style.setProperty('--eyk-sg-title-height', `${height}px`);
      };
      this.titleResizeObserver = new ResizeObserver(sync);
      this.titleResizeObserver.observe(title);
      this.titleOpenObserver = new MutationObserver(sync);
      this.titleOpenObserver.observe(this.modal, { attributes: true, attributeFilter: ['open'] });
      window.addEventListener('resize', sync, { signal });
      sync();
    }

    bindDiagramBounds(signal) {
      const svgs = [...this.modal.querySelectorAll('[data-guide-svg]')];
      this.queueDiagramBounds = () => {
        cancelAnimationFrame(this.diagramFrame);
        this.diagramFrame = requestAnimationFrame(() => {
          if (!this.modal.hasAttribute('open') || signal.aborted) return;
          const displayedWidth = Math.min(...svgs.map((svg) => svg.getBoundingClientRect().width));
          if (!(displayedWidth > 32)) return;
          // At 21 SVG units, allow more than a full line height between pointer labels.
          const smallPhone = window.matchMedia('(max-width: 359.98px)').matches;
          svgs.forEach((svg) => svg.querySelectorAll('[data-pointer]').forEach((pointer) => {
            const index = ['templelength', 'nosebridge', 'lenswidth'].indexOf(pointer.dataset.pointer);
            const x = [158.5, 137.3, 110.3][index];
            const y = Number(pointer.dataset.pointerTop) + (smallPhone ? 14 : 0) + index * (smallPhone ? 28 : 22);
            pointer.querySelector('path').setAttribute('d', `M${x} ${pointer.dataset.pointerStart} V${y} H196`);
            pointer.querySelector('text').setAttribute('y', String(y + 5));
          }));
          // getBBox on a group includes transformed text and the original path data.
          // Measure bold as well so hover/focus never changes the crop or clips a label.
          const boxes = svgs.map((svg) => {
            const groups = [...svg.querySelectorAll('.eyk-size-guide__art, [data-line], [data-pointer], .eyk-size-guide__inside-marking')];
            return groups.map((group) => {
              const active = group.classList.contains('is-active');
              if (group.hasAttribute('data-line') || group.hasAttribute('data-pointer')) group.classList.add('is-active');
              const box = group.getBBox();
              group.classList.toggle('is-active', active);
              return box;
            });
          });
          const centers = svgs.map((svg) => {
            const art = svg.querySelector('.eyk-size-guide__art').getBBox();
            return art.x + art.width / 2;
          });
          // Each crop is symmetric about its art, including the furthest label on either side.
          const halfSpan = Math.max(...boxes.flatMap((items, index) => items.map((box) =>
            Math.max(centers[index] - box.x, box.x + box.width - centers[index]))));
          // Share one width, reserving 8 SVG units plus 5 CSS px for focus and .5px stroke.
          const width = Math.ceil((2 * halfSpan + 16) / (1 - 11 / displayedWidth));
          const padding = 8 + 5.5 * width / displayedWidth;
          svgs.forEach((svg, index) => {
            const left = centers[index] - width / 2;
            const top = Math.floor(Math.min(...boxes[index].map((box) => box.y)) - padding);
            const bottom = Math.ceil(Math.max(...boxes[index].map((box) => box.y + box.height)) + padding);
            svg.setAttribute('viewBox', `${left} ${top} ${width} ${bottom - top}`);
            svg.setAttribute('data-bounds-ready', '');
          });
        });
      };
      let previousWidth = 0;
      this.diagramResizeObserver = new ResizeObserver((entries) => {
        const width = entries[0].contentRect.width;
        if (width !== previousWidth) {
          previousWidth = width;
          this.queueDiagramBounds();
        }
      });
      this.diagramResizeObserver.observe(this.modal.querySelector('.eyk-size-guide__columns'));
      this.diagramOpenObserver = new MutationObserver(this.queueDiagramBounds);
      this.diagramOpenObserver.observe(this.modal, { attributes: true, attributeFilter: ['open'] });
      window.addEventListener('resize', this.queueDiagramBounds, { signal });
      document.fonts?.ready.then(() => { if (!signal.aborted) this.queueDiagramBounds(); });
    }

    bindPurchase(signal) {
      this.atc = this.modal.querySelector('[data-guide-atc]');
      if (!this.atc) return;
      this.productForm = document.getElementById(this.atc.getAttribute('form'));
      if (!this.productForm?.matches('form.js-product-form')) {
        this.atc.disabled = true;
        return;
      }
      const sync = () => {
        const button = this.productForm.querySelector('button[name="add"]');
        this.atc.disabled = !button || button.disabled || button.getAttribute('aria-disabled') === 'true';
        if (button) this.atc.textContent = this.atc.disabled ? button.textContent.trim() : 'Add to cart';
      };
      this.purchaseObserver = new MutationObserver(sync);
      this.purchaseObserver.observe(this.productForm, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['disabled', 'aria-disabled'] });
      sync();
      this.productForm.addEventListener('submit', () => {
        // Let all stock submit listeners run first, including its AJAX cart handler.
        queueMicrotask(() => {
          if (!signal.aborted && this.modal.hasAttribute('open')) this.modal.close();
        });
      }, { signal });
    }

    bindHighlights(signal) {
      this.pinnedDimension = null;
      this.lastTouchAt = -Infinity;
      this.openedAt = -Infinity;
      this.keyboardHighlight = false;
      const mouse = window.matchMedia('(hover: hover) and (pointer: fine)');
      const recentTouch = () => Date.now() - this.lastTouchAt < 700;
      document.addEventListener('pointerdown', (event) => {
        this.keyboardHighlight = false;
        if (event.pointerType === 'touch') this.lastTouchAt = Date.now();
      }, { capture: true, signal });
      document.addEventListener('keydown', (event) => {
        if (['Tab', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key)) {
          this.keyboardHighlight = true;
        }
      }, { capture: true, signal });
      [...this.rows, ...this.lines, ...this.pointers].forEach((element) => {
        const key = element.dataset.dimension || element.dataset.line || element.dataset.pointer;
        if (element.tagName !== 'BUTTON' && !element.dataset.pointer) element.setAttribute('tabindex', '0');
        const hoverAllowed = (event) => mouse.matches && event.pointerType === 'mouse'
          && !recentTouch() && Date.now() - this.openedAt >= 700;
        element.addEventListener('pointerenter', (event) => {
          if (hoverAllowed(event)) this.highlight(key);
        }, { signal });
        element.addEventListener('pointerleave', (event) => {
          if (hoverAllowed(event)) this.highlight(this.pinnedDimension);
        }, { signal });
        element.addEventListener('focus', () => {
          if (this.keyboardHighlight) this.highlight(key);
        }, { signal });
        element.addEventListener('blur', () => this.highlight(this.pinnedDimension), { signal });
        const toggle = () => {
          this.pinnedDimension = this.pinnedDimension === key ? null : key;
          this.highlight(this.pinnedDimension);
        };
        let touchId = null;
        element.addEventListener('pointerdown', (event) => {
          if (event.pointerType === 'touch') touchId = event.pointerId;
        }, { signal });
        element.addEventListener('pointercancel', () => { touchId = null; }, { signal });
        element.addEventListener('pointerup', (event) => {
          if (event.pointerType !== 'touch') return;
          this.lastTouchAt = Date.now();
          const hit = document.elementFromPoint(event.clientX, event.clientY);
          if (touchId === event.pointerId && hit?.closest('[data-dimension], [data-line], [data-pointer]') === element) toggle();
          touchId = null;
        }, { signal });
        element.addEventListener('click', (event) => {
          if (event.pointerType === 'touch' || recentTouch()) return;
          toggle();
        }, { signal });
        element.addEventListener('keydown', (event) => {
          if (element.tagName !== 'BUTTON' && ['Enter', ' '].includes(event.key)) {
            event.preventDefault();
            toggle();
          }
        }, { signal });
      });
    }

    highlight(key) {
      [...this.rows, ...this.lines, ...this.pointers].forEach((element) => {
        element.classList.toggle('is-active', (element.dataset.dimension || element.dataset.line || element.dataset.pointer) === key);
      });
    }

    convertMmCopy(copy) {
      return copy.replace(/(\d+(?:[.,]\d+)?)\s*mm\b/g, (_, value) =>
        `${EykSizeGuide.format(Number(value.replace(',', '.')), this.unit)} ${this.unit}`);
    }

    renderCopy() {
      this.modal.querySelectorAll('[data-total-only-copy]').forEach((node) => {
        node.textContent = EykSizeGuide.tokens(node.dataset.totalOnlyCopy, { unit: this.unit });
      });
      this.modal.querySelectorAll('[data-mm-copy]').forEach((node) => {
        node.textContent = this.convertMmCopy(node.dataset.mmCopy);
      });
    }

    placeInlineTrigger() {
      const section = this.closest('.section-main-product, .shopify-section');
      const column = this.closest('.product-column-right');
      if (!section || !column || !section.contains(column)) return;
      const preceding = [...column.querySelectorAll('.shopify-app-block')].filter((block) =>
        block.closest('.section-main-product, .shopify-section') === section
        && (block.compareDocumentPosition(this) & Node.DOCUMENT_POSITION_FOLLOWING)
        && block.querySelector('.pl-swatches__label'));
      this.swatchBlock = preceding.pop();
      if (!this.swatchBlock) return;
      this.triggerHome = document.createComment('Size guide trigger home');
      this.trigger.before(this.triggerHome);
      this.guideRow = this.closest('.lightly-spaced-row');
      const sync = () => {
        if (!this.isConnected) return;
        this.swatchObserver.disconnect();
        const label = this.swatchBlock.querySelector('.pl-swatches__label');
        if (label) {
          // A render may clone the old markup; retain our one original trigger.
          this.swatchBlock.querySelectorAll('open-or-scroll-to').forEach((trigger) => {
            if (trigger !== this.trigger && trigger.dataset.targetId === this.trigger.dataset.targetId) trigger.remove();
          });
          if (this.inlineLabel !== label) this.inlineLabel?.classList.remove('eyk-size-guide-label');
          this.inlineLabel = label;
          label.classList.add('eyk-size-guide-label');
          // Preserve the app's value node: it may update that same node later.
          const options = [...label.querySelectorAll('.eyk-swatch-option')];
          let option = options.shift();
          options.forEach((duplicate) => duplicate.remove());
          const walker = document.createTreeWalker(label, NodeFilter.SHOW_TEXT, {
            acceptNode: (node) => node.parentElement.closest('.eyk-swatch-option, open-or-scroll-to')
              ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT
          });
          let textNode;
          while ((textNode = walker.nextNode())) {
            const prefix = textNode.data.match(/^\s*([^:\n]{1,40}:)\s*/);
            if (!prefix) continue;
            if (!option) {
              option = document.createElement('span');
              option.className = 'eyk-swatch-option';
            }
            option.textContent = prefix[1];
            textNode.before(option);
            textNode.data = textNode.data.slice(prefix[0].length);
            break;
          }
          this.opener.classList.add('eyk-size-guide__link--inline');
          if (this.trigger.parentElement !== label) label.append(this.trigger);
          this.classList.add('eyk-size-guide--inline');
          this.guideRow?.classList.add('eyk-size-guide-row--inline');
        } else {
          this.restoreInlineTrigger();
        }
        this.swatchObserver.observe(this.swatchBlock, { childList: true, characterData: true, subtree: true });
      };
      this.swatchObserver = new MutationObserver(sync);
      sync();
    }

    restoreInlineTrigger() {
      if (this.triggerHome?.parentNode && this.trigger?.parentNode !== this.triggerHome.parentNode) {
        this.triggerHome.after(this.trigger);
      }
      this.inlineLabel?.classList.remove('eyk-size-guide-label');
      this.opener?.classList.remove('eyk-size-guide__link--inline');
      this.classList.remove('eyk-size-guide--inline');
      this.guideRow?.classList.remove('eyk-size-guide-row--inline');
    }

    keepInputVisible() {
      cancelAnimationFrame(this.focusFrame);
      this.focusFrame = requestAnimationFrame(() => {
        const input = document.activeElement;
        if (this.modal.hasAttribute('open') && input?.matches('input') && this.modal.contains(input)) {
          input.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        }
      });
    }

    static templeResult(label, productMm, currentMm, length = false) {
      // Round the magnitude before the inclusive 1mm boundary, in either direction.
      const delta = productMm - currentMm;
      const magnitude = Math.round((Math.abs(delta) + Number.EPSILON) * 10) / 10;
      const direction = delta > 0 ? (length ? 'longer' : 'wider') : (length ? 'shorter' : 'narrower');
      return `${label}: about ${magnitude <= 1 ? 'the same as your pair' : `${magnitude} mm ${direction} than your pair`}.`;
    }

    static pairCell(productMm, currentMm, unit = 'mm') {
      const magnitude = EykSizeGuide.format(Math.abs(productMm - currentMm), unit);
      const sign = magnitude === '0' ? '' : productMm > currentMm ? '+' : '−';
      return `${EykSizeGuide.format(currentMm, unit)} ${unit} · ${sign}${magnitude} ${unit}`;
    }

    revealComparison() {
      this.modal.querySelector('.eyk-spec__table').classList.add('has-comparison');
      this.modal.querySelectorAll('[data-your-pair]').forEach((cell) => { cell.hidden = false; });
    }

    bindTempleCompare(signal) {
      this.templeForm = this.modal.querySelector('[data-temple-compare]');
      this.templeSubmitted = false;
      this.templeFields = [...this.modal.querySelectorAll('[data-temple-field]')].map((input) => {
        const key = input.dataset.templeField;
        const row = this.rows.find((item) => item.dataset.dimension === key);
        input.addEventListener('input', () => {
          input.removeAttribute('aria-invalid');
          if (this.templeSubmitted) this.compareTemple();
        }, { signal });
        input.addEventListener('focus', () => this.keepInputVisible(), { signal });
        return { input, row, key, productMm: EykSizeGuide.parse(row?.dataset.mm) };
      });
      this.templeForm?.addEventListener('submit', (event) => {
        event.preventDefault();
        this.templeSubmitted = true;
        this.compareTemple();
        this.templeFields.find((field) => field.input.hasAttribute('aria-invalid'))?.input.focus();
      }, { signal });
    }

    compareTemple() {
      const error = this.templeForm.querySelector('[data-temple-error]');
      const outcome = this.templeForm.querySelector('[data-temple-outcome]');
      const results = this.templeForm.querySelector('[data-temple-results]');
      const entered = this.templeFields.filter(({ input }) => input.value.trim() !== '');
      const invalid = entered.filter(({ input }) => EykSizeGuide.parse(input.value, 'mm') === null);
      this.templeFields.forEach(({ input, row }) => {
        input.removeAttribute('aria-invalid');
        if (row) row.querySelector('[data-your-pair]').textContent = '';
      });
      results.replaceChildren();
      if (!entered.length || invalid.length) {
        error.textContent = invalid.length
          ? 'Enter numbers only, for example 50.'
          : 'Enter at least one number from the inside of your temple, for example 50.';
        invalid.forEach(({ input }) => input.setAttribute('aria-invalid', 'true'));
        error.hidden = false;
        outcome.hidden = true;
        return;
      }
      entered.forEach(({ input, row, key, productMm }) => {
        const mm = EykSizeGuide.parse(input.value, 'mm');
        const result = document.createElement('p');
        result.textContent = EykSizeGuide.templeResult(input.dataset.label, productMm, mm, key === 'templelength');
        results.append(result);
        row.querySelector('[data-your-pair]').textContent = EykSizeGuide.pairCell(productMm, mm, 'mm');
      });
      this.revealComparison();
      error.hidden = true;
      outcome.hidden = false;
    }

    clearError(field) {
      field.input.removeAttribute('aria-invalid');
      field.form.querySelector('[data-error]').hidden = true;
    }

    compare(field) {
      const error = field.form.querySelector('[data-error]');
      const outcome = field.form.querySelector('[data-total-outcome]');
      if (field.mm === null || field.productMm === null) {
        outcome.hidden = true;
        field.form.querySelector('[data-result]').textContent = '';
        if (field.row) field.row.querySelector('[data-your-pair]').textContent = '';
        error.textContent = field.mm === null
          ? `Enter one number greater than 0 in ${this.unit}, for example ${this.unit === 'in' ? '5.5' : '140'}.`
          : 'This measurement is not available for this frame.';
        error.hidden = false;
        if (field.mm === null) field.input.setAttribute('aria-invalid', 'true');
        return;
      }
      this.clearError(field);
      const difference = EykSizeGuide.difference(field.productMm, field.mm, false, this.unit);
      const delta = field.productMm - field.mm;
      // Preserve S1's rounding, inclusive 2mm boundary and custom templates.
      const tokens = {
        ...difference, difference: this.unit === 'in'
          ? (Math.abs(Math.round(delta * 10) / 10) / 25.4).toFixed(2)
          : String(Math.round(Math.abs(Math.round(delta * 10) / 10))),
        current: EykSizeGuide.format(field.mm, this.unit), product: this.dataset.product,
        product_value: EykSizeGuide.format(field.productMm, this.unit), unit: this.unit,
        margin: EykSizeGuide.format(Number(this.dataset.marginMm) || 0, this.unit)
      };
      const legacyDefault = 'Your current pair: [current] [unit] · [product]: [product_value] [unit] · [comparison].';
      const template = this.dataset.resultTemplate;
      const magnitude = Math.abs(Math.round(delta * 10) / 10);
      const verdict = magnitude <= 2 ? this.dataset.verdictSame : this.dataset.verdictSlightly;
      field.form.querySelector('[data-result]').textContent = EykSizeGuide.tokens(
        template && template !== legacyDefault ? template : verdict, tokens
      );
      const marginTemplate = this.convertMmCopy(this.dataset.marginTemplate.replace(/\[margin\]\s*mm\b/g, '[margin] [unit]'));
      const margin = field.form.querySelector('[data-margin]');
      if (margin) {
        margin.textContent = EykSizeGuide.tokens(marginTemplate, tokens);
        margin.hidden = margin.textContent.trim() === '';
      }
      this.revealComparison();
      field.row.querySelector('[data-your-pair]').textContent = EykSizeGuide.pairCell(field.productMm, field.mm, this.unit);
      outcome.hidden = false;
    }

    changeUnit(unit) {
      if (unit === this.unit || !['mm', 'in'].includes(unit)) return;
      const invalid = this.fields.find((field) => field.input.value.trim() !== '' && field.mm === null);
      if (invalid) {
        invalid.submitted = true;
        this.compare(invalid);
        invalid.input.focus();
        return;
      }
      this.unit = unit;
      this.fields.forEach((field) => {
        if (field.mm !== null) field.input.value = EykSizeGuide.format(field.mm, unit);
        if (field.submitted) this.compare(field);
      });
      this.renderUnits();
      this.renderCopy();
    }

    renderUnits() {
      this.fields.forEach(({ input }) => { input.placeholder = this.unit === 'in' ? '5.5' : '140'; });
      this.modal.querySelectorAll('[data-fit-scale]').forEach((scale) => {
        scale.setAttribute('aria-label', `Fit: ${scale.dataset.fitPhrase}; ${EykSizeGuide.format(Number(scale.dataset.fitMm), this.unit)} ${this.unit} on a scale from narrow to wide`);
      });
      this.modal.querySelectorAll('[data-unit]').forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.unit === this.unit)));
      this.modal.querySelectorAll('[data-input-unit]').forEach((label) => { label.textContent = this.unit; });
      this.rows.forEach((row) => {
        const mm = EykSizeGuide.parse(row.dataset.mm);
        row.querySelector('[data-measurement]').textContent = mm === null ? 'Not available' : `${EykSizeGuide.format(mm, this.unit)} ${this.unit}`;
      });
      this.lines.forEach((line) => {
        const mm = EykSizeGuide.parse(line.dataset.mm);
        const label = line.querySelector('[data-dimension-label]');
        const value = mm === null ? '' : `${EykSizeGuide.format(mm, this.unit)} ${this.unit}`;
        const valueLine = label.querySelector('[data-dimension-value]');
        if (valueLine) {
          label.querySelector('[data-dimension-name]').textContent = line.dataset.label;
          valueLine.textContent = value;
        } else {
          label.textContent = line.dataset.label + (value ? ` ${value}` : '');
        }
        line.classList.toggle('is-missing', mm === null);
      });
      this.queueDiagramBounds?.();
    }
  }
  customElements.define('eyk-size-guide', EykSizeGuide);
}
